import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { User, Calendar, CheckCircle } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useLanguage } from '@/context/language-context';

interface UserProfile {
  id: string;
  display_name: string | null;
  created_at: string | null;
  is_verified_client?: boolean | null; // Champ optionnel
  avatar?: string | null; // Ajouter avatar pour cohérence
}

interface UserProfileModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  userProfile: UserProfile | null;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({
  isOpen,
  onOpenChange,
  userProfile,
}) => {
  const { t, currentLanguage } = useLanguage();
  const locale = currentLanguage === 'fr' ? fr : enUS;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('common.unknown');
    try {
      const date = new Date(dateString);
      return format(date, 'PPP', { locale }); // Format comme "15 avr. 2024"
    } catch (error) {
      console.error("Error formatting date:", error);
      return t('common.unknown');
    }
  };

  const formatRelativeDate = (dateString: string | null) => {
    if (!dateString) return t('common.unknown');
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale }); // Format comme "il y a environ 2 mois"
    } catch (error) {
      console.error("Error formatting relative date:", error);
      return t('common.unknown');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('profile.client_profile')}</DialogTitle>
          <DialogDescription>
            {t('profile.client_profile_desc')}
          </DialogDescription>
        </DialogHeader>
        
        {userProfile ? (
          <div className="grid gap-4 py-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={userProfile.avatar || "/placeholder-user.png"} alt={userProfile.display_name || 'User'} />
                <AvatarFallback>
                  {userProfile.display_name?.slice(0, 2).toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="text-lg font-semibold flex items-center">
                  {userProfile.display_name || t('common.anonymous_user')}
                  {userProfile.is_verified_client && (
                    <CheckCircle className="ml-2 h-5 w-5 text-green-500" />
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">ID: {userProfile.id}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center">
                <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('profile.member_since')}: {formatDate(userProfile.created_at)}
                  ({formatRelativeDate(userProfile.created_at)})
                </span>
              </div>
              {/* Ajouter d'autres informations si nécessaire */}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-24">
             <p className="text-muted-foreground">{t('common.loading_profile')}</p>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfileModal; 